import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Search, Bell, HelpCircle, Crown, ShieldCheck, MessageCircle, Send, Briefcase, FileCheck, Award, GraduationCap, Zap, CheckCircle2 } from 'lucide-react';
import api from '../utils/api';
import { slugFor } from '../utils/slug';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SharedHero from '../components/layout/SharedHero';
import AdSlot from '../components/common/AdSlot';
import SeoHead, { generateWebSiteSchema, generateOrganizationSchema } from '../components/common/SeoHead';
import { WHATSAPP_CHANNEL_URL, TELEGRAM_CHANNEL_URL, CONFIGURED_CHANNELS } from '../config/social';

// Every content type the hero search covers, paired with the public list
// endpoint that can actually search it (all five accept ?search=) and the
// route its detail page lives at.
const SEARCH_SOURCES = [
  { type: 'job', endpoint: '/api/jobs', basePath: 'jobs' },
  { type: 'admit-card', endpoint: '/api/admit-cards', basePath: 'admit-cards' },
  { type: 'result', endpoint: '/api/results', basePath: 'results' },
  { type: 'answer-key', endpoint: '/api/answerkeys', basePath: 'answer-key' },
  { type: 'scholarship', endpoint: '/api/scholarships', basePath: 'scholarships' }
];

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_RESULTS_PER_TYPE = 5;

// Every listing page reads ?q=, so Enter can hand off to the one that matches
// what the user actually searched for.
const LIST_PATH_BY_TYPE = {
  'job': '/jobs',
  'admit-card': '/admit-cards',
  'result': '/results',
  'answer-key': '/answer-key',
  'scholarship': '/scholarships'
};

// The money line on a homepage yojana row. Both halves are hand-typed display
// strings, so nothing is parsed or formatted - this mirrors benefitSuffix() in
// pages/YojanaPage.js so the same scheme reads identically on both surfaces.
const yojanaBenefitSuffix = (yojana) => {
  const min = (yojana?.benefitAmount?.minAmount || '').trim();
  const max = (yojana?.benefitAmount?.maxAmount || '').trim();
  if (min && max && min !== max) return `${min} - ${max}`;
  return min || max || '';
};

const HomePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const searchRef = useRef(null);
  const searchRequestId = useRef(0);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  // Fetch unified homepage data in a single parallel API round-trip
  const { data: homepageData, isLoading: homepageLoading } = useQuery(
    ['homepage-data-batch'],
    async () => {
      const res = await api.get('/api/homepage');
      return res.data;
    },
    { staleTime: 5 * 60 * 1000 } // 5 minutes
  );

  // Map individual loading states to the single batch request loading state
  const jobsLoading = homepageLoading;
  const admitCardsLoading = homepageLoading;
  const resultsLoading = homepageLoading;
  const answerKeysLoading = homepageLoading;
  const scholarshipsLoading = homepageLoading;
  const yojanaLoading = homepageLoading;
  const notificationsLoading = homepageLoading;

  // Extract data from the batch response
  const latestJobs = homepageData?.jobs || [];
  const closingSoon = homepageData?.closingSoon || [];
  const latestAdmitCards = homepageData?.admitCards || [];
  const latestResults = homepageData?.results || [];
  const latestAnswerKeys = homepageData?.answerKeys || [];
  const latestScholarships = homepageData?.scholarships || [];
  // backend/routes/homepage.js returns this list under the key `yojana`
  // (singular) - not `yojanas`.
  const latestYojanas = homepageData?.yojana || [];
  const notificationsData = { data: homepageData?.notifications || [] };

  // Debounced, backend-backed search. This used to filter only the handful of
  // records the /api/homepage batch had already loaded, so a query for any exam
  // outside that batch reported "No results found" even though the record
  // existed in the database.
  useEffect(() => {
    const query = searchQuery.trim();

    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchFailed(false);
      setShowSearchResults(false);
      return;
    }

    const requestId = ++searchRequestId.current;
    const controller = new AbortController();

    setShowSearchResults(true);
    setSearchLoading(true);

    const timer = setTimeout(async () => {
      const perType = await Promise.all(
        SEARCH_SOURCES.map(source =>
          api
            .get(source.endpoint, {
              params: { page: 1, limit: SEARCH_RESULTS_PER_TYPE, search: query },
              signal: controller.signal
            })
            .then(res => ({
              ok: true,
              items: (res.data?.data || []).map(item => ({
                ...item,
                type: source.type,
                detailPath: `/${source.basePath}/${slugFor(item)}`
              }))
            }))
            // One content type failing must not blank the whole dropdown or
            // throw an unhandled rejection out of the page. The ok flag is what
            // separates "nothing matched" from "nothing answered" below.
            .catch(() => ({ ok: false, items: [] }))
        )
      );

      // Ignore a stale response: a slow earlier request could otherwise resolve
      // last and overwrite the results for the query the user moved on to.
      if (requestId !== searchRequestId.current) return;

      setSearchResults(perType.flatMap(r => r.items));
      // Every request failing (API down, rate limited, offline) used to render
      // the same "No results found" as a genuinely empty search - telling the
      // student their exam does not exist when the search never ran.
      setSearchFailed(!perType.some(r => r.ok));
      setSearchLoading(false);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;

    // Hand off to the listing page for whichever content type actually matched.
    // Enter always went to /jobs before, so searching an admit card or a result
    // - which the dropdown had just listed - landed on an empty jobs page.
    const countByType = searchResults.reduce((acc, r) => {
      acc[r.type] = (acc[r.type] || 0) + 1;
      return acc;
    }, {});
    const bestType = Object.keys(countByType).sort((a, b) => countByType[b] - countByType[a])[0];

    setShowSearchResults(false);
    navigate(`${LIST_PATH_BY_TYPE[bestType] || '/jobs'}?q=${encodeURIComponent(query)}`);
  };

  const handleSearchInputChange = (val) => {
    const value = typeof val === 'string' ? val : (val && val.target ? val.target.value : '');
    setSearchQuery(value);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const homeFaqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Sarkari Adda 24x7 kya hai aur yeh candidates ke liye kaise beneficial hai?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sarkari Adda 24x7 (SarkariAdda24x7.com) ek dedicated government alerts updates aggregation portal hai. Hum daily primary notifications check karte hain taaki aapko latest govt jobs, admit cards, results, answer keys and scholarships ek unified screen par provide ho sakein.'
        }
      },
      {
        '@type': 'Question',
        name: 'Sarkari Adda 24x7 par updates kitne authentic hote hain?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sarkari Adda 24x7 par listed updates 100% verified hain. Hum target notifications, age limit and fees details sirf official departments (jaise SSC, UPSC, State Selection Boards) ki websites aur official gazettes se fetch karne ke baad hi publish karte hain.'
        }
      },
      {
        '@type': 'Question',
        name: 'Personalized government notifications alert directly email par kaise receive kar sakte hain?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Aap humare platform par Premium Subscription choose kar sakte hain. Activation ke baad, aap custom filters set karke state, eligibility and exam matches direct apne inbox me notification alerts receive kar sakte hain.'
        }
      },
      {
        '@type': 'Question',
        name: 'Sarkari Adda 24x7 par notification details check karne ka process kya hai?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Aap kisi bhi listed post par click karke uski complete requirements details dekh sakte hain, jahan page ke bottom me official apply online, notification pdf aur official department links active hote hain.'
        }
      }
    ]
  };

  return (
    <>
      <SeoHead
        title={`Sarkari Adda 24x7 — Government Jobs, Results, Admit Cards & Answer Keys ${currentYear}`}
        description={`Get latest government job notifications ${currentYear}, admit cards, exam results, official answer keys and scholarships on Sarkari Adda 24x7. Apply online for SSC, UPSC, Railway, Banking, Police and State PSC recruitment.`}
        keywords={`sarkari adda 24x7, sarkari adda 247, sarkariadda24x7, sarkari adda, government jobs ${currentYear}, sarkari result, admit card, answer key, SSC, UPSC, Railway, Police jobs`}
        canonicalPath="/"
        breadcrumbs={[{ name: 'Home', url: '/' }]}
        schema={[generateWebSiteSchema(), generateOrganizationSchema(), homeFaqSchema]}
      />

      {/* Unified Hero & Search Section */}
      <div ref={searchRef}>
        <SharedHero
          pageType="home"
          searchQuery={searchQuery}
          setSearchQuery={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
        >
          {/* Search Results Dropdown */}
          {showSearchResults && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gov-line rounded-xl shadow-card-hover z-50 max-h-96 overflow-y-auto">
              {searchLoading ? (
                <div className="px-4 py-6 text-center">
                  <LoadingSpinner size="md" className="mx-auto mb-2" />
                  <p className="text-sm text-gov-body">Searching for "{searchQuery}"...</p>
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map((result, index) => (
                  <Link
                    key={`${result.type}-${result._id || result.id}`}
                    to={result.detailPath}
                    className="block px-4 py-3 min-h-[44px] hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
                    onClick={() => {
                      setShowSearchResults(false);
                      setSearchQuery('');
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="sa-pill sa-pill-info">
                            {result.type === 'job' && 'Job'}
                            {result.type === 'admit-card' && 'Admit Card'}
                            {result.type === 'result' && 'Result'}
                            {result.type === 'answer-key' && 'Answer Key'}
                            {result.type === 'scholarship' && 'Scholarship'}
                          </span>
                          <span className="text-sm font-semibold text-gov-ink">
                            {result.title}
                          </span>
                        </div>
                        <p className="text-xs text-gov-body mt-1">
                          {result.organization}
                        </p>
                      </div>
                      <div className="text-2xs text-gov-muted">
                        Click to view details →
                      </div>
                    </div>
                  </Link>
                ))
              ) : searchFailed ? (
                <div className="px-4 py-6 text-center">
                  <Search className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-gov-body">Search is not responding right now</p>
                  <p className="text-2xs text-gov-muted mt-1">Check your connection and try again in a moment</p>
                </div>
              ) : (
                <div className="px-4 py-6 text-center">
                  <Search className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-gov-body">No results found for "{searchQuery}"</p>
                  <p className="text-2xs text-gov-muted mt-1">Try searching with different keywords</p>
                </div>
              )}
            </div>
          )}
        </SharedHero>
      </div>

      {/* Page Body Wrapper with Claude background */}
      <div className="bg-gov-paper min-h-screen pb-12">

        {/* Colorful Important Notifications Section — four boxes per desktop row. */}
        <div className="w-full px-4 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" aria-label="Important notifications">
            {notificationsLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="min-h-[44px] bg-slate-200 rounded-lg animate-pulse" aria-hidden="true" />
              ))
            ) : (() => {
              const liveItems = (notificationsData?.data && notificationsData.data.length > 0)
                ? notificationsData.data
                : [
                    ...latestJobs.slice(0, 4).map(j => ({
                      _id: j._id || j.id,
                      title: j.title.replace(/\s*[–-]\s*\d+[\d,]*\s*(Vacancies?|Posts?)\s*$/i, ''),
                      link: `/jobs/${slugFor(j)}`,
                      color: 'yellow'
                    })),
                    ...latestAdmitCards.slice(0, 2).map(a => ({
                      _id: a._id || a.id,
                      title: a.title.replace(/\s*[–-]\s*\d+[\d,]*\s*(Vacancies?|Posts?)\s*$/i, ''),
                      link: `/admit-cards/${slugFor(a)}`,
                      color: 'blue'
                    })),
                    ...latestResults.slice(0, 2).map(r => ({
                      _id: r._id || r.id,
                      title: r.title.replace(/\s*[–-]\s*\d+[\d,]*\s*(Vacancies?|Posts?)\s*$/i, ''),
                      link: `/results/${slugFor(r)}`,
                      color: 'red'
                    }))
                  ];

              // Keep the original four-box desktop row even when a content type
              // has no freshly published record. A category link fills only the
              // missing slots; real/admin notifications always come first.
              const categoryFallbacks = [
                { _id: 'latest-jobs-link', title: 'Latest Government Jobs', link: '/jobs', color: 'yellow' },
                { _id: 'latest-admit-cards-link', title: 'Latest Admit Cards', link: '/admit-cards', color: 'blue' },
                { _id: 'latest-results-link', title: 'Latest Sarkari Results', link: '/results', color: 'red' },
                { _id: 'latest-answer-keys-link', title: 'Latest Answer Keys', link: '/answer-key', color: 'green' }
              ];
              const hasCategory = (route) => liveItems.some(item =>
                typeof item.link === 'string' && item.link.startsWith(route)
              );
              const items = [
                ...liveItems,
                ...categoryFallbacks.filter(item => !hasCategory(item.link))
              ].slice(0, 4);

              const colorPalette = [
                'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white',
                'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white',
                'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white',
                'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white',
                'bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-700 hover:to-rose-800 text-white',
                'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white',
                'bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white',
                'bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white'
              ];

              const colorMap = {
                yellow: 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white',
                blue: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white',
                orange: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white',
                red: 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white',
                green: 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white',
                pink: 'bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white',
                sky: 'bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white'
              };

              return items.map((notification, index) => {
                const selectedColor = (notification.color || notification.notificationColor || '').toLowerCase();
                const bgClass = colorMap[selectedColor] || colorPalette[index % colorPalette.length];

                return (
                  <Link
                    key={notification._id || index}
                    to={notification.link || '/jobs'}
                    onClick={async () => {
                      if ('clicks' in notification && notification._id) {
                        try {
                          await api.post(`/api/notifications/${notification._id}/click`);
                        } catch (e) {
                          console.error('Error tracking click:', e);
                        }
                      }
                    }}
                    className={`flex items-center justify-center min-h-[44px] px-3 py-2.5 ${bgClass} rounded-lg font-bold text-2xs md:text-xs text-center shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02]`}
                  >
                    {notification.title}
                  </Link>
                );
              });
            })()}
          </div>
        </div>

        {/* Main Content Sections */}
        <div className="w-full px-4 pt-1 pb-6 space-y-4 md:space-y-4.5">

          {/* Sponsored Ad Banner */}
          <AdSlot type="banner" />

          {/* Closing Soon - applications whose last date falls within 7 days.
              Deadline urgency is the strongest repeat-visit driver in this
              niche, and every major competitor surfaces it. */}
          {closingSoon.length > 0 && (
            <section className="sa-section" aria-labelledby="closing-soon-heading">
              <div className="bg-gradient-to-r from-rose-700 to-red-600 text-white px-3.5 py-2 flex items-center gap-2">
                <span aria-hidden="true">⏳</span>
                <h2 id="closing-soon-heading" className="text-xs md:text-sm font-bold uppercase tracking-wider">
                  Closing Soon — Apply Before Last Date
                </h2>
              </div>
              <ul className="p-2 md:p-3 divide-y divide-slate-100">
                {closingSoon.map((job) => (
                  <li key={job._id}>
                    <Link
                      to={`/jobs/${slugFor(job)}`}
                      className="flex items-start justify-between gap-2 py-2.5 min-h-[44px] md:py-2 px-1 rounded-lg hover:bg-rose-50/60 transition-colors"
                    >
                      <span className="flex items-start gap-2 flex-1 min-w-0">
                        <span className="w-1.5 h-1.5 bg-rose-500 rounded-full flex-shrink-0 mt-1.5" aria-hidden="true"></span>
                        <span className="min-w-0">
                          <span className="block text-xs md:text-sm font-semibold text-blue-700 line-clamp-2 md:line-clamp-1">
                            {job.title}
                          </span>
                          {job.organization && (
                            <span className="block text-2xs text-gov-muted mt-0.5">{job.organization}</span>
                          )}
                        </span>
                      </span>
                      {job.importantDates?.lastDateApply && (
                        <span className="text-xs text-rose-700 font-bold whitespace-nowrap flex-shrink-0 mt-0.5">
                          {job.importantDates.lastDateApply}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Row 1: Jobs Notification, Admit Cards & Results (3 Columns Horizontally) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4">

            {/* 1. Jobs Notification Section */}
            <div className="sa-section flex flex-col justify-between">
              <div>
                {/* Integrated Header */}
                <div className="px-3 py-2 md:py-2.5 text-center bg-gov-navy border-b border-gov-line">
                  <h2 className="font-heading text-xs md:text-sm lg:text-[14.5px] font-bold text-white tracking-wide">
                    Latest Jobs Notification
                  </h2>
                  <p className="font-heading font-medium text-slate-300 text-[11px] hidden md:block mt-0.5">
                    Apply for the latest government job opportunities
                  </p>
                </div>

                {/* Content */}
                <div className="h-[316px] overflow-hidden p-2 md:h-[268px] md:p-3">
                  {jobsLoading ? (
                    <div className="min-h-[280px] md:min-h-[240px] space-y-1 md:space-y-3 py-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2 min-h-[44px] md:min-h-0 py-1.5">
                          <div className="w-1.5 h-1.5 bg-slate-300 rounded-full flex-shrink-0"></div>
                          <div className="h-4 bg-slate-200/80 rounded animate-pulse w-4/5"></div>
                        </div>
                      ))}
                    </div>
                  ) : latestJobs.length > 0 ? (
                    <div>
                      {latestJobs.slice(0, 6).map(job => (
                        <div key={job._id || job.id} className="flex items-center md:items-start gap-2 py-0.5 md:py-1.5 px-1 rounded hover:bg-slate-50 transition-colors">
                          <div className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0 md:mt-1.5"></div>
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/jobs/${slugFor(job)}`}
                              className="flex flex-wrap items-center gap-x-1.5 min-h-[44px] md:min-h-0 py-1.5 md:py-0 font-heading text-[12px] font-semibold text-blue-700 hover:text-blue-900 leading-snug transition-colors"
                            >
                              <span className="line-clamp-2 md:line-clamp-1">{job.title.replace(/\s*[–-]\s*\d+[\d,]*\s*(Vacancies?|Posts?)\s*$/i, '')}</span>
                              {(job.totalPosts || job.vacancy) && (
                                <span className="text-red-600 font-bold text-[10.5px] whitespace-nowrap font-heading">
                                  {(job.totalPosts || job.vacancy).toLocaleString()} Vacancies
                                </span>
                              )}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="font-heading text-slate-500 text-xs">No jobs available at the moment.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* View All Link */}
              <div className="px-3 py-1.5 text-center border-t border-gov-line mt-2">
                  <Link
                    to="/jobs"
                    className="inline-flex items-center justify-center font-heading text-[11.5px] font-bold text-gov-navy hover:text-gov-saffron transition-colors"
                  >
                    View All Jobs →
                  </Link>
                </div>
            </div>

            {/* 2. Admit Cards Section */}
            <div className="sa-section flex flex-col justify-between">
              <div>
                {/* Integrated Header */}
                <div className="px-3 py-2 md:py-2.5 text-center bg-gov-navy border-b border-gov-line">
                  <h2 className="font-heading text-xs md:text-sm lg:text-[14.5px] font-bold text-white tracking-wide">
                    Latest Admit Card Release
                  </h2>
                  <p className="font-heading font-medium text-slate-300 text-[11px] hidden md:block mt-0.5">
                    Download admit cards for upcoming examinations
                  </p>
                </div>

                {/* Content */}
                <div className="h-[316px] overflow-hidden p-2 md:h-[268px] md:p-3">
                  {admitCardsLoading ? (
                    <div className="min-h-[280px] md:min-h-[240px] space-y-1 md:space-y-3 py-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2 min-h-[44px] md:min-h-0 py-1.5">
                          <div className="w-1.5 h-1.5 bg-slate-300 rounded-full flex-shrink-0"></div>
                          <div className="h-4 bg-slate-200/80 rounded animate-pulse w-4/5"></div>
                        </div>
                      ))}
                    </div>
                  ) : latestAdmitCards.length > 0 ? (
                    <div>
                      {latestAdmitCards.slice(0, 6).map(admitCard => (
                        <div key={admitCard._id || admitCard.id} className="flex items-center md:items-start gap-2 py-0.5 md:py-1.5 px-1 rounded hover:bg-slate-50 transition-colors">
                          <div className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0 md:mt-1.5"></div>
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/admit-cards/${slugFor(admitCard)}`}
                              className="flex flex-wrap items-center gap-x-1.5 min-h-[44px] md:min-h-0 py-1.5 md:py-0 font-heading text-[12px] font-semibold text-blue-700 hover:text-blue-900 leading-snug transition-colors"
                            >
                              <span className="line-clamp-2 md:line-clamp-1">{admitCard.title.replace(/\s*[–-]\s*\d+[\d,]*\s*(Vacancies?|Posts?)\s*$/i, '')}</span>
                              {admitCard.totalPosts && (
                                <span className="text-red-600 font-bold text-[10.5px] whitespace-nowrap font-heading">
                                  {admitCard.totalPosts} Vacancies
                                </span>
                              )}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="font-heading text-slate-500 text-xs">No admit cards available at the moment.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* View All Link */}
              <div className="px-3 py-1.5 text-center border-t border-gov-line mt-2">
                  <Link
                    to="/admit-cards"
                    className="inline-flex items-center justify-center font-heading text-[11.5px] font-bold text-gov-navy hover:text-gov-saffron transition-colors"
                  >
                    View All Admit Cards →
                  </Link>
                </div>
            </div>

            {/* 3. Results Section */}
            <div className="sa-section flex flex-col justify-between">
              <div>
                {/* Integrated Header */}
                <div className="px-3 py-2 md:py-2.5 text-center bg-gov-navy border-b border-gov-line">
                  <h2 className="font-heading text-xs md:text-sm lg:text-[14.5px] font-bold text-white tracking-wide">
                    Latest Result Announcements
                  </h2>
                  <p className="font-heading font-medium text-slate-300 text-[11px] hidden md:block mt-0.5">
                    Check results for government examinations across India
                  </p>
                </div>

                {/* Content */}
                <div className="h-[316px] overflow-hidden p-2 md:h-[268px] md:p-3">
                  {resultsLoading ? (
                    <div className="min-h-[280px] md:min-h-[240px] space-y-1 md:space-y-3 py-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2 min-h-[44px] md:min-h-0 py-1.5">
                          <div className="w-1.5 h-1.5 bg-slate-300 rounded-full flex-shrink-0"></div>
                          <div className="h-4 bg-slate-200/80 rounded animate-pulse w-4/5"></div>
                        </div>
                      ))}
                    </div>
                  ) : latestResults.length > 0 ? (
                    <div>
                      {latestResults.slice(0, 6).map(result => (
                        <div key={result._id || result.id} className="flex items-center md:items-start gap-2 py-0.5 md:py-1.5 px-1 rounded hover:bg-slate-50 transition-colors">
                          <div className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0 md:mt-1.5"></div>
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/results/${slugFor(result)}`}
                              className="flex flex-wrap items-center gap-x-1.5 min-h-[44px] md:min-h-0 py-1.5 md:py-0 font-heading text-[12px] font-semibold text-blue-700 hover:text-blue-900 leading-snug transition-colors"
                            >
                              <span className="line-clamp-2 md:line-clamp-1">{result.title.replace(/\s*[–-]\s*\d+[\d,]*\s*(Vacancies?|Posts?)\s*$/i, '')}</span>
                              {result.totalCandidates && (
                                <span className="text-red-600 font-bold text-[10.5px] whitespace-nowrap font-heading">
                                  {result.totalCandidates} Candidates
                                </span>
                              )}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="font-heading text-slate-500 text-xs">No results available at the moment.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* View All Link */}
              <div className="px-3 py-1.5 text-center border-t border-gov-line mt-2">
                  <Link
                    to="/results"
                    className="inline-flex items-center justify-center font-heading text-[11.5px] font-bold text-gov-navy hover:text-gov-saffron transition-colors"
                  >
                    View All Results →
                  </Link>
                </div>
            </div>

          </div>

          {/* Row 2: Answer Keys, Scholarships & Sarkari Yojana (3 Columns Horizontally) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4">

            {/* 4. Answer Keys Section */}
            <div className="sa-section flex flex-col justify-between">
              <div>
                {/* Integrated Header */}
                <div className="px-3 py-2 md:py-2.5 text-center bg-gov-navy border-b border-gov-line">
                  <h2 className="font-heading text-xs md:text-sm lg:text-[14.5px] font-bold text-white tracking-wide">
                    Latest Answer Keys
                  </h2>
                  <p className="font-heading font-medium text-slate-300 text-[11px] hidden md:block mt-0.5">
                    Download official answer keys
                  </p>
                </div>

                {/* Content */}
                <div className="h-[316px] overflow-hidden p-2 md:h-[268px] md:p-3">
                  {answerKeysLoading ? (
                    <div className="min-h-[280px] md:min-h-[240px] space-y-1 md:space-y-3 py-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2 min-h-[44px] md:min-h-0 py-1.5">
                          <div className="w-1.5 h-1.5 bg-slate-300 rounded-full flex-shrink-0"></div>
                          <div className="h-4 bg-slate-200/80 rounded animate-pulse w-4/5"></div>
                        </div>
                      ))}
                    </div>
                  ) : latestAnswerKeys.length > 0 ? (
                    <div>
                      {latestAnswerKeys.slice(0, 6).map(answerKey => (
                        <div key={answerKey._id || answerKey.id} className="flex items-center md:items-start gap-2 py-0.5 md:py-1.5 px-1 rounded hover:bg-slate-50 transition-colors">
                          <div className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0 md:mt-1.5"></div>
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/answer-key/${slugFor(answerKey)}`}
                              className="flex flex-wrap items-center gap-x-1.5 min-h-[44px] md:min-h-0 py-1.5 md:py-0 font-heading text-[12px] font-semibold text-blue-700 hover:text-blue-900 leading-snug transition-colors"
                            >
                              <span className="line-clamp-2 md:line-clamp-1">{answerKey.title.replace(/\s*[–-]\s*\d+[\d,]*\s*(Vacancies?|Posts?)\s*$/i, '')}</span>
                              {answerKey.totalPosts && (
                                <span className="text-red-600 font-bold text-[10.5px] whitespace-nowrap font-heading">
                                  {answerKey.totalPosts} Vacancies
                                </span>
                              )}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="font-heading text-slate-500 text-xs">No answer keys available at the moment.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* View All Link */}
              <div className="px-3 py-1.5 text-center border-t border-gov-line mt-2">
                  <Link
                    to="/answer-key"
                    className="inline-flex items-center justify-center font-heading text-[11.5px] font-bold text-gov-navy hover:text-gov-saffron transition-colors"
                  >
                    View All Answer Keys →
                  </Link>
                </div>
            </div>

            {/* 5. Scholarships Section */}
            <div className="sa-section flex flex-col justify-between">
              <div>
                {/* Integrated Header */}
                <div className="px-3 py-2 md:py-2.5 text-center bg-gov-navy border-b border-gov-line">
                  <h2 className="font-heading text-xs md:text-sm lg:text-[14.5px] font-bold text-white tracking-wide">
                    Student Scholarships
                  </h2>
                  <p className="font-heading font-medium text-slate-300 text-[11px] hidden md:block mt-0.5">
                    Financial schemes for students
                  </p>
                </div>

                {/* Content */}
                <div className="h-[316px] overflow-hidden p-2 md:h-[268px] md:p-3">
                  {scholarshipsLoading ? (
                    <div className="min-h-[280px] md:min-h-[240px] space-y-1 md:space-y-3 py-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2 min-h-[44px] md:min-h-0 py-1.5">
                          <div className="w-1.5 h-1.5 bg-slate-300 rounded-full flex-shrink-0"></div>
                          <div className="h-4 bg-slate-200/80 rounded animate-pulse w-4/5"></div>
                        </div>
                      ))}
                    </div>
                  ) : latestScholarships.length > 0 ? (
                    <div>
                      {latestScholarships.slice(0, 6).map(scholarship => (
                        <div key={scholarship._id || scholarship.id} className="flex items-center md:items-start gap-2 py-0.5 md:py-1.5 px-1 rounded hover:bg-slate-50 transition-colors">
                          <div className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0 md:mt-1.5"></div>
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/scholarships/${slugFor(scholarship)}`}
                              className="flex flex-wrap items-center gap-x-1.5 min-h-[44px] md:min-h-0 py-1.5 md:py-0 font-heading text-[12px] font-semibold text-blue-700 hover:text-blue-900 leading-snug transition-colors"
                            >
                              <span className="line-clamp-2 md:line-clamp-1">{scholarship.title.replace(/\s*[–-]\s*\d+[\d,]*\s*(Vacancies?|Posts?)\s*$/i, '')}</span>
                              {scholarship.scholarshipAmount?.minAmount && (
                                <span className="text-red-600 font-bold text-[10.5px] whitespace-nowrap font-heading">
                                  Min: ₹{scholarship.scholarshipAmount.minAmount}
                                </span>
                              )}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="font-heading text-slate-500 text-xs">No scholarships available at the moment.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* View All Link */}
              <div className="px-3 py-1.5 text-center border-t border-gov-line mt-2">
                  <Link
                    to="/scholarships"
                    className="inline-flex items-center justify-center font-heading text-[11.5px] font-bold text-gov-navy hover:text-gov-saffron transition-colors"
                  >
                    View All Scholarships →
                  </Link>
                </div>
            </div>

            {/* 6. Sarkari Yojana Section */}
            <div className="sa-section flex flex-col justify-between">
              <div>
                {/* Integrated Header */}
                <div className="px-3 py-2 md:py-2.5 text-center bg-gov-navy border-b border-gov-line">
                  <h2 className="font-heading text-xs md:text-sm lg:text-[14.5px] font-bold text-white tracking-wide">
                    Sarkari Yojana
                  </h2>
                  <p className="font-heading font-medium text-slate-300 text-[11px] hidden md:block mt-0.5">
                    Central & state welfare schemes
                  </p>
                </div>

                {/* Content */}
                <div className="h-[316px] overflow-hidden p-2 md:h-[268px] md:p-3">
                  {yojanaLoading ? (
                    <div className="min-h-[280px] md:min-h-[240px] space-y-1 md:space-y-3 py-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2 min-h-[44px] md:min-h-0 py-1.5">
                          <div className="w-1.5 h-1.5 bg-slate-300 rounded-full flex-shrink-0"></div>
                          <div className="h-4 bg-slate-200/80 rounded animate-pulse w-4/5"></div>
                        </div>
                      ))}
                    </div>
                  ) : latestYojanas.length > 0 ? (
                    <div>
                      {latestYojanas.slice(0, 6).map(yojana => {
                        const benefit = yojanaBenefitSuffix(yojana);
                        return (
                          <div key={yojana._id || yojana.id} className="flex items-center md:items-start gap-2 py-0.5 md:py-1.5 px-1 rounded hover:bg-slate-50 transition-colors">
                            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0 md:mt-1.5"></div>
                            <div className="flex-1 min-w-0">
                              <Link
                                to={`/sarkari-yojana/${slugFor(yojana)}`}
                                className="flex flex-wrap items-center gap-x-1.5 min-h-[44px] md:min-h-0 py-1.5 md:py-0 font-heading text-[12px] font-semibold text-blue-700 hover:text-blue-900 leading-snug transition-colors"
                              >
                                <span className="line-clamp-2 md:line-clamp-1">{yojana.title.replace(/\s*[–-]\s*\d+[\d,]*\s*(Vacancies?|Posts?)\s*$/i, '')}</span>
                                {benefit ? (
                                  <span className="text-emerald-700 font-bold text-[10.5px] whitespace-nowrap font-heading">
                                    ₹{benefit}
                                  </span>
                                ) : yojana.state ? (
                                  <span className="text-amber-700 font-bold text-[10.5px] whitespace-nowrap font-heading">
                                    {yojana.state}
                                  </span>
                                ) : null}
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="font-heading text-slate-500 text-xs">No schemes available at the moment.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* View All Link */}
              <div className="px-3 py-1.5 text-center border-t border-gov-line mt-2">
                  <Link
                    to="/sarkari-yojana"
                    className="inline-flex items-center justify-center font-heading text-[11.5px] font-bold text-gov-navy hover:text-gov-saffron transition-colors"
                  >
                    View All Yojana →
                  </Link>
                </div>
            </div>

          </div>

          {/* Horizontal Quick Actions & Internal Linking Strip */}
          <div className="pt-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
              
              {/* 1. Smart Email Job Alerts */}
              <Link
                to="/smart-alerts"
                className="flex items-center gap-2.5 p-2.5 bg-white hover:bg-emerald-50/50 border border-slate-200/90 hover:border-emerald-300 rounded-lg shadow-2xs hover:shadow-xs transition-all duration-150 group"
              >
                <div className="w-8 h-8 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shrink-0 group-hover:scale-105 transition-transform">
                  <Bell className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <span className="font-heading text-[12px] font-bold text-slate-800 group-hover:text-emerald-700 block truncate">
                    Smart Job Alerts
                  </span>
                  <span className="font-heading text-[10.5px] text-slate-500 block truncate">
                    Direct Email Alerts
                  </span>
                </div>
              </Link>

              {/* 2. Go Premium Membership */}
              <Link
                to="/premium-plans"
                className="flex items-center gap-2.5 p-2.5 bg-white hover:bg-amber-50/50 border border-slate-200/90 hover:border-amber-300 rounded-lg shadow-2xs hover:shadow-xs transition-all duration-150 group"
              >
                <div className="w-8 h-8 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0 group-hover:scale-105 transition-transform">
                  <Crown className="w-4 h-4 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <span className="font-heading text-[12px] font-bold text-slate-800 group-hover:text-amber-700 block truncate">
                    Go Premium
                  </span>
                  <span className="font-heading text-[10.5px] text-slate-500 block truncate">
                    Exclusive Plans
                  </span>
                </div>
              </Link>

              {/* 3. Frequently Asked Questions */}
              <Link
                to="/faq"
                className="flex items-center gap-2.5 p-2.5 bg-white hover:bg-blue-50/50 border border-slate-200/90 hover:border-blue-300 rounded-lg shadow-2xs hover:shadow-xs transition-all duration-150 group"
              >
                <div className="w-8 h-8 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shrink-0 group-hover:scale-105 transition-transform">
                  <HelpCircle className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <span className="font-heading text-[12px] font-bold text-slate-800 group-hover:text-blue-700 block truncate">
                    FAQ Center
                  </span>
                  <span className="font-heading text-[10.5px] text-slate-500 block truncate">
                    Exam & Portal Q&As
                  </span>
                </div>
              </Link>

              {/* 4. Verification & Disclaimer */}
              <Link
                to="/declaration"
                className="flex items-center gap-2.5 p-2.5 bg-white hover:bg-slate-100/70 border border-slate-200/90 hover:border-slate-300 rounded-lg shadow-2xs hover:shadow-xs transition-all duration-150 group"
              >
                <div className="w-8 h-8 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 shrink-0 group-hover:scale-105 transition-transform">
                  <ShieldCheck className="w-4 h-4 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <span className="font-heading text-[12px] font-bold text-slate-800 group-hover:text-slate-900 block truncate">
                    Authenticity
                  </span>
                  <span className="font-heading text-[10.5px] text-slate-500 block truncate">
                    100% Verified Info
                  </span>
                </div>
              </Link>

              {/* 5. Support & Helpdesk */}
              <Link
                to="/contact"
                className="flex items-center gap-2.5 p-2.5 bg-white hover:bg-purple-50/50 border border-slate-200/90 hover:border-purple-300 rounded-lg shadow-2xs hover:shadow-xs transition-all duration-150 group col-span-2 sm:col-span-1"
              >
                <div className="w-8 h-8 rounded-md bg-purple-50 border border-purple-200 flex items-center justify-center text-purple-600 shrink-0 group-hover:scale-105 transition-transform">
                  <MessageCircle className="w-4 h-4 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <span className="font-heading text-[12px] font-bold text-slate-800 group-hover:text-purple-700 block truncate">
                    Candidate Support
                  </span>
                  <span className="font-heading text-[10.5px] text-slate-500 block truncate">
                    Get Editorial Help
                  </span>
                </div>
              </Link>

            </div>
          </div>

          {/* Unified Portal Information, FAQs & Community Section */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-5 md:p-7 mt-6 shadow-xs space-y-7">

            {/* 1. About Sarkari Adda & Key Features */}
            <div>
              <div className="mb-5 text-center max-w-4xl mx-auto">
                <h2 className="font-heading text-base md:text-lg lg:text-[20px] font-black text-slate-900 tracking-tight mb-2">
                  Sarkari Adda 24x7 – Your Ultimate Hub for Government Updates
                </h2>
                <p className="font-heading text-[12px] md:text-[12.5px] text-slate-600 leading-relaxed font-normal">
                  Sarkari Adda 24x7 (SarkariAdda24x7.com) Bharat ke students, aspirants aur job seekers ke liye ek trusted aur comprehensive platform hai, jo aapko latest Sarkari Jobs, Admit Cards, Results, Answer Keys aur Scholarships ki timely aur verified notifications provide karta hai. Humara mission hai ki aap apni career planning aur exam preparation ko efficiently manage kar sakein, bina multiple websites search kiye.
                </p>
              </div>

              {/* 6-Card Key Features Grid (3 Columns) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-4">
                
                {/* Feature 1 */}
                <div className="bg-slate-50/80 hover:bg-white hover:border-blue-300 border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs hover:shadow-xs group hover:scale-[1.01]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-lg bg-blue-100/80 text-blue-700 flex items-center justify-center shrink-0">
                      <Briefcase className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="font-heading text-[12.5px] font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                      Sarkari Jobs Notifications
                    </h3>
                  </div>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal">
                    SSC, Banking, Railway, State & Central Government jobs ke liye har vacancy ka updated information ek hi jagah milega. Hum eligibility criteria, exam dates aur online links ke saath complete guide provide karte hain.
                  </p>
                </div>

                {/* Feature 2 */}
                <div className="bg-slate-50/80 hover:bg-white hover:border-blue-300 border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs hover:shadow-xs group hover:scale-[1.01]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-lg bg-amber-100/80 text-amber-700 flex items-center justify-center shrink-0">
                      <FileCheck className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="font-heading text-[12.5px] font-bold text-slate-900 group-hover:text-amber-700 transition-colors">
                      Admit Cards Updates
                    </h3>
                  </div>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal">
                    Har exam ka admit card release hone ke baad, Sarkari Adda 24x7 aapko step-by-step download guide aur official direct server links timely provide karta hai taaki aapka preparation bina delay smoothly continue rahe.
                  </p>
                </div>

                {/* Feature 3 */}
                <div className="bg-slate-50/80 hover:bg-white hover:border-blue-300 border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs hover:shadow-xs group hover:scale-[1.01]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100/80 text-emerald-700 flex items-center justify-center shrink-0">
                      <Award className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="font-heading text-[12.5px] font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">
                      Results & Answer Keys
                    </h3>
                  </div>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal">
                    Exams complete hone ke baad, hum official scorecards aur answer keys ki latest updates real-time provide karte hain. Answer keys ke saath marking scheme aur objections link directly access kar sakte hain.
                  </p>
                </div>

                {/* Feature 4 */}
                <div className="bg-slate-50/80 hover:bg-white hover:border-blue-300 border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs hover:shadow-xs group hover:scale-[1.01]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-lg bg-purple-100/80 text-purple-700 flex items-center justify-center shrink-0">
                      <GraduationCap className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="font-heading text-[12.5px] font-bold text-slate-900 group-hover:text-purple-700 transition-colors">
                      Scholarships & Alerts
                    </h3>
                  </div>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal">
                    Students ke liye Central aur State government scholarships ki notifications eligibility criteria, benefit amounts aur application process ke saath verified form me available hoti hain.
                  </p>
                </div>

                {/* Feature 5 */}
                <div className="bg-slate-50/80 hover:bg-white hover:border-blue-300 border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs hover:shadow-xs group hover:scale-[1.01]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-lg bg-indigo-100/80 text-indigo-700 flex items-center justify-center shrink-0">
                      <Zap className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="font-heading text-[12.5px] font-bold text-slate-900 group-hover:text-indigo-700 transition-colors">
                      User-Friendly Interface
                    </h3>
                  </div>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal">
                    Sarkari Adda 24x7 ka design simple, clean aur fast hai. Mobile aur desktop par aap exam-wise, state-wise ya qualification-wise easily direct information access kar sakte hain.
                  </p>
                </div>

                {/* Feature 6 */}
                <div className="bg-slate-50/80 hover:bg-white hover:border-blue-300 border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs hover:shadow-xs group hover:scale-[1.01]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-lg bg-teal-100/80 text-teal-700 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="font-heading text-[12.5px] font-bold text-slate-900 group-hover:text-teal-700 transition-colors">
                      100% Authentic & Verified
                    </h3>
                  </div>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal">
                    Sabhi listings direct official departmental gazettes, notifications aur official portals se verify hone ke baad hi publish ki jaati hain taaki koi bhi fake update na mile.
                  </p>
                </div>

              </div>
            </div>

            {/* 2. Frequently Asked Questions (FAQs) */}
            <div className="border-t border-slate-200/80 pt-6">
              <div className="mb-4 text-center max-w-4xl mx-auto">
                <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 text-center flex items-center justify-center gap-1.5">
                  <span className="text-[#f97316] font-extrabold text-[16px]">|</span>
                  Frequently Asked Questions (FAQs)
                </h2>
                <p className="font-heading text-[11.5px] text-slate-500 font-normal mt-0.5">
                  Common queries answered about exam updates, authentic sources & personalized notifications
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                
                {/* FAQ Item 1 */}
                <div className="bg-slate-50/80 hover:bg-white border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs">
                  <h3 className="font-heading text-[12.5px] font-bold text-slate-900 mb-1 flex items-start gap-1.5">
                    <span className="text-blue-600 font-bold">Q.</span>
                    <span>Sarkari Adda 24x7 kya hai aur yeh candidates ke liye kaise beneficial hai?</span>
                  </h3>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal pl-4">
                    Sarkari Adda 24x7 (SarkariAdda24x7.com) ek dedicated government alerts updates aggregation portal hai. Hum daily primary notifications check karte hain taaki aapko latest govt jobs, admit cards, results, answer keys and scholarships ek unified screen par provide ho sakein.
                  </p>
                </div>

                {/* FAQ Item 2 */}
                <div className="bg-slate-50/80 hover:bg-white border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs">
                  <h3 className="font-heading text-[12.5px] font-bold text-slate-900 mb-1 flex items-start gap-1.5">
                    <span className="text-blue-600 font-bold">Q.</span>
                    <span>Sarkari Adda 24x7 par updates kitne authentic hote hain?</span>
                  </h3>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal pl-4">
                    Sarkari Adda 24x7 par listed updates 100% verified hain. Hum target notifications, age limit and fees details sirf official departments (jaise SSC, UPSC, State Selection Boards) ki websites aur official gazettes se fetch karne ke baad hi publish karte hain.
                  </p>
                </div>

                {/* FAQ Item 3 */}
                <div className="bg-slate-50/80 hover:bg-white border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs">
                  <h3 className="font-heading text-[12.5px] font-bold text-slate-900 mb-1 flex items-start gap-1.5">
                    <span className="text-blue-600 font-bold">Q.</span>
                    <span>Personalized government notifications alert directly email par kaise receive kar sakte hain?</span>
                  </h3>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal pl-4">
                    Aap humare platform par <Link to="/premium-plans" className="text-blue-600 font-bold hover:underline">Premium Subscription</Link> choose kar sakte hain. Activation ke baad, aap custom filters set karke state, eligibility and exam matches direct apne inbox me notification alerts receive kar sakte hain.
                  </p>
                </div>

                {/* FAQ Item 4 */}
                <div className="bg-slate-50/80 hover:bg-white border border-slate-200/90 rounded-xl p-3.5 transition-all shadow-2xs">
                  <h3 className="font-heading text-[12.5px] font-bold text-slate-900 mb-1 flex items-start gap-1.5">
                    <span className="text-blue-600 font-bold">Q.</span>
                    <span>Sarkari Adda 24x7 par notification details check karne ka process kya hai?</span>
                  </h3>
                  <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed font-normal pl-4">
                    Aap kisi bhi listed post par click karke uski complete requirements details dekh sakte hain, jahan page ke bottom me official apply online, notification pdf aur official department links active hote hain.
                  </p>
                </div>

              </div>
            </div>

            {/* 3. Real-time Alert & Social Community Hub */}
            <div className="bg-gradient-to-r from-slate-50 via-blue-50/40 to-slate-50 border border-blue-200/80 rounded-xl p-5 md:p-6 text-center shadow-2xs">
              <div className="max-w-2xl mx-auto space-y-1.5 mb-4">
                <h3 className="font-heading text-base md:text-lg font-bold text-slate-900 text-center flex items-center justify-center gap-1.5">
                  <span className="text-[#f97316] font-extrabold text-[16px]">|</span>
                  {CONFIGURED_CHANNELS && CONFIGURED_CHANNELS.length > 0
                    ? `Join ${CONFIGURED_CHANNELS.join(' & ')} for Instant Updates`
                    : 'Join Telegram & WhatsApp for Instant Updates'}
                </h3>
                <p className="font-heading text-[11.5px] text-slate-600 leading-relaxed text-center font-normal">
                  Get real-time notifications for jobs, admit cards, results & scholarships delivered straight to your phone and email inbox!
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2.5">
                <a
                  href={WHATSAPP_CHANNEL_URL || 'https://whatsapp.com/channel'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 h-[34px] px-3.5 bg-[#25d366] hover:bg-[#20ba5a] text-white font-heading text-[11.5px] font-bold rounded-lg transition-all shadow-xs hover:scale-102 cursor-pointer"
                  aria-label="Join our WhatsApp Channel"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                  </svg>
                  <span>WhatsApp Channel</span>
                </a>

                <a
                  href={TELEGRAM_CHANNEL_URL || 'https://t.me/sarkari_adda'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 h-[34px] px-3.5 bg-[#0088cc] hover:bg-[#0077b5] text-white font-heading text-[11.5px] font-bold rounded-lg transition-all shadow-xs hover:scale-102 cursor-pointer"
                  aria-label="Join our Telegram Channel"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Telegram Channel</span>
                </a>

                <Link
                  to="/premium-plans"
                  className="inline-flex items-center justify-center gap-1.5 h-[34px] px-3.5 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:from-amber-600 hover:to-orange-600 text-white font-heading text-[11.5px] font-bold rounded-lg transition-all shadow-xs hover:scale-102"
                >
                  <Crown className="w-3.5 h-3.5 text-amber-200" />
                  <span>Smart Email Alerts</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
        <AdSlot type="mobile_sticky" />
      </div>
    </>
  );
};

export default HomePage;
